import React, { useEffect, useState, useContext } from "react";
import { PatientContext } from "../../lib/main-context";
import { useTranslation } from "../../i18n/TranslationProvider";

// hooks
import { useHistory, useLocation } from "react-router-dom";
import useListSelector from "../../lib/useListSelector";

// components
import Sallie from "../UIComponents/Sallie";
import ListSelector from "../UIComponents/ListSelector";
import SubmitButton from "../UIComponents/SubmitButton";
import OnboardingStepHeader from "./OnboardingStepHeader";

// api
import {
  createHistoryMeds,
  createFamilyHistory,
  getConditionFormData,
} from "../../api/TBIRequests";

// lib
import customizeMessages from "../../lib/customizeMessages";
import { Form, Col } from "react-bootstrap";

const RecentCondition2 = ({ setOnboardingPercent, user }) => {
  const { patient } = useContext(PatientContext);
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [returnedData, setReturnedData] = useState([]);
  const [errorMessage, setErrorMessage] = useState(false);

  const history = useHistory();
  const location = useLocation();

  const [fields, setFields] = useState([
    { medication: "", curUsing: "", tolerate: "" },
  ]);

  const handleAddField = () => {
    setFields([...fields, { medication: "", curUsing: "", tolerate: "" }]);
  };

  const handleDeleteField = (index) => {
    const newFields = [...fields];
    newFields.splice(index, 1);
    setFields(newFields);
  };

  const handleChange = (event, index, field) => {
    const newFields = [...fields];
    newFields[index][field] = event.target.value;
    setFields(newFields);
  };

  const handleTypeChange = (event, index) => {
    const newFields = [...fields];
    newFields[index].curUsing = event.target.value;
    setFields(newFields);
  };

  const handleOptionChange = (event, index) => {
    const newFields = [...fields];
    newFields[index].tolerate = event.target.value;
    setFields(newFields);
  };

  const {
    toggleFromSelectedList,
    switchSingleSelection,
    addOther,
    selectedData,
    initialize,
  } = useListSelector({
    setInitialData: setReturnedData,
    initialData: returnedData,
  });

  // Translate a family history item at render time
  const tFamilyOption = (item) => {
    const key = `recentcondition2.family.options.${item}`;
    const translated = t(key);
    return translated !== key ? translated : item;
  };

  const onSubmit = async () => {
    const assignedData = {
      familyHistoryOptions: selectedData.familyHistory,
    };
    setSubmitting(true);
    try {
      if (fields.length !== 0) {
        await createHistoryMeds({ patientId: patient.patientId, data: fields });
      }
      await createFamilyHistory({ patientId: patient?.patientId, data: assignedData });
      history.push("/laterSymptoms", {
        fromPatientControl: location.state?.fromPatientControl,
      });
    } catch (err) {
      console.error("An error occurred:", err);
      history.push("/oops");
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      try {
        const response = await getConditionFormData();
        const { data } = response;
        if (isMounted) {
          // Store raw items — translate at render time via tFamilyOption
          const processedData = data.map((itemList) => {
            const { category, items } = itemList;
            return { category, items };
          });
          setReturnedData(processedData);
          initialize(processedData);
          setLoading(false);
        }
      } catch (err) {
        if (isMounted) {
          history.push("/oops");
          setLoading(false);
        }
      }
    };
    fetchData();
    return () => { isMounted = false; };
  }, []);

  const getPossibleMessages = () => ({
    mainHeader: {
      tbiPatient: t("recentcondition2.header"),
      caregiver: t("recentcondition2.header"),
    },
    sallieText: {
      tbiPatient: t("recentcondition2.sallie.patient"),
      caregiver: t("recentcondition2.sallie.caregiver"),
    },
    familyHistory: {
      tbiPatient: t("recentcondition2.family.patient"),
      caregiver: t("recentcondition2.family.caregiver"),
    },
    medications: {
      tbiPatient: t("recentcondition2.medication.patient"),
      caregiver: t("recentcondition2.medication.caregiver"),
    },
  });

  const getMessage = customizeMessages({ user, getPossibleMessages });

  const options = {
    familyHistory: { message: "familyHistory" },
  };

  return (
    <>
      {loading ? (
        t("common.loading")
      ) : (
        <div className="d-block">
          <OnboardingStepHeader text={getMessage("mainHeader")} />
          <Sallie text={getMessage("sallieText")} />

          {returnedData.map((itemList, i) => {
            const { category } = itemList;
            // Translate items at render time
            const translatedItems = itemList.items.map(tFamilyOption);
            return (
              <ListSelector
                key={category}
                category={category}
                message={getMessage(options[category].message)}
                list={translatedItems}
                index={i}
                maxSelectAmount={options[category].responseNumber}
                selectedList={selectedData[category]}
                toggleFromSelectedList={toggleFromSelectedList}
                selectOne={switchSingleSelection}
                addOther={addOther}
              />
            );
          })}

          <div className="listSelector">
            <Form.Label className={"h6-label"}>
              {getMessage("medications")}
            </Form.Label>

            {fields.map((field, index) => (
              <Form.Row key={index} className="listSelector">
                <Form.Group as={Col} md={3}>
                  <Form.Label>{t("recentcondition2.medication.type")}</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder={t("recentcondition2.medication.enter")}
                    value={field.medication}
                    onChange={(event) => handleChange(event, index, "medication")}
                  />
                </Form.Group>

                <Form.Group as={Col} md={3}>
                  <Form.Label>{t("recentcondition2.medication.currently_using")}</Form.Label>
                  <Col>
                    <Form.Check
                      inline
                      label={t("recentcondition2.medication.yes")}
                      type="radio"
                      name={`radio-type-${index}`}
                      id={`field-${index}-yes`}
                      value="yes"
                      checked={field.curUsing === "yes"}
                      onChange={(event) => handleTypeChange(event, index)}
                    />
                    <Form.Check
                      inline
                      label={t("recentcondition2.medication.no")}
                      type="radio"
                      name={`radio-type-${index}`}
                      id={`field-${index}-no`}
                      value="no"
                      checked={field.curUsing === "no"}
                      onChange={(event) => handleTypeChange(event, index)}
                    />
                  </Col>
                </Form.Group>

                <Form.Group as={Col} md={5}>
                  <Form.Label>{t("recentcondition2.medication.tolerate")}</Form.Label>
                  <Col>
                    <Form.Check
                      inline
                      label={t("recentcondition2.medication.no_problems")}
                      type="radio"
                      name={`radio-option-${index}`}
                      id={`field-${index}-option-a`}
                      value="No Problems"
                      checked={field.tolerate === "No Problems"}
                      onChange={(event) => handleOptionChange(event, index)}
                    />
                    <Form.Check
                      className="pt-2"
                      inline
                      label={t("recentcondition2.medication.side_effects")}
                      type="radio"
                      name={`radio-option-${index}`}
                      id={`field-${index}-option-b`}
                      value="Experienced Side Effects"
                      checked={field.tolerate === "Experienced Side Effects"}
                      onChange={(event) => handleOptionChange(event, index)}
                    />
                  </Col>
                </Form.Group>
                <SubmitButton variant="primary" onClick={() => handleDeleteField(index)}>
                  X
                </SubmitButton>
              </Form.Row>
            ))}
            <Form.Row>
              <SubmitButton variant="primary" onClick={handleAddField}>
                +
              </SubmitButton>
            </Form.Row>
          </div>

          {errorMessage && (
            <Form.Control.Feedback style={{ display: "block" }} type="invalid">
              {t("recentcondition2.choose.error")}
            </Form.Control.Feedback>
          )}
          <SubmitButton disabled={submitting} onClick={onSubmit} type="submit">
            {t("onboarding.saveContinue")} &gt;
          </SubmitButton>
        </div>
      )}
    </>
  );
};

export default RecentCondition2;